import   { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { Info as InfoIcon, AccountBalance as BankIcon } from '@mui/icons-material';
import { virtualBankAccountAPI } from '../services/api';
 

// eslint-disable-next-line react/prop-types
const FundingEventsTable = ({ customerId }) => {

  const [fundingEvents, setFundingEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);



  useEffect(() => {
    const fetchFundingEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await virtualBankAccountAPI.getFundingEvents(customerId, { limit: 20 });
        
        if (response.data.success) {
          setFundingEvents(response.data.data.funding_events || []);
        } else {
          setError('Failed to fetch funding events');
        }
      } catch (err) {
        console.error('Error fetching funding events:', err);
        setError(err.response?.data?.error || 'Failed to fetch funding events');
      } finally {
        setLoading(false);
      }
    };
    if (customerId) {
      fetchFundingEvents();
    }
  }, [customerId]);



  const handleInfoClick = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const formatCurrency = (amount) => {
    return `$${amount?.toFixed(2)}`;
  };

 

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Amount</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Sender</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Network</TableCell> 
              <TableCell align="center">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fundingEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No funding events found for this customer
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              fundingEvents.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(event.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {event.lastUpdate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {event.senderName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={event.type} 
                      size="small" 
                      variant="outlined"
                      color={event.type === 'funded' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={event.network.toUpperCase()} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                
                  <TableCell align="center">
                    <Tooltip title="View funding event details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleInfoClick(event)}
                        color="primary"
                      >
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Funding Event Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BankIcon color="primary" />
            <Typography variant="h6">Funding Event Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                {selectedEvent.formattedAmount}
              </Typography>
              
              <Box display="flex" gap={4} mb={3}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Last update
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.lastUpdate}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Customer
                  </Typography>
                  <Typography variant="body1" color="primary">
                    {selectedEvent.customerId}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                Funding details
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.formattedAmount}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="textSecondary">
                    Sender name
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.senderName}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="textSecondary">
                    Network
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.network}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="textSecondary">
                    Reference
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.reference}
                  </Typography>
                </Box>
           
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="textSecondary">
                    Type
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.type}
                  </Typography>
                </Box>
                
                {selectedEvent.description && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedEvent.description}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FundingEventsTable;